'use client';

import { useEffect, useState } from 'react';
import { useAllowedTransitions, useUpdateJobStatus } from '@/hooks/usePreInspection';
import { PreInspectionApiError } from '@/lib/pre-inspection/api';
import { arePreInspectionTransitionsEnabled } from '@/lib/pre-inspection/flags';
import {
  isAssignmentTransition,
  type AssignmentTransitionCode,
} from '@/lib/pre-inspection/assignment';
import {
  isAppointmentTransition,
  type AppointmentTransitionCode,
} from '@/lib/pre-inspection/appointment';
import { AssignmentActionModal } from './AssignmentActionModal';
import { AppointmentActionModal } from './AppointmentActionModal';

// User types permitted to PATCH /jobs/{id}/status. Mirrors
// require_job_update_permission in job-service: excludes SUPER_ADMIN and
// TENANT_ADMIN so we don't render buttons that would 403.
export const TRANSITION_ALLOWED_USER_TYPES = [
  'CLAIMS_ADJUSTER',
  'VERIFIER',
  'INSURER_USER',
  'INSURER_ADMIN',
] as const;

function normalize(code: string | null | undefined): string {
  return (code ?? '').trim().toUpperCase();
}

// allowed_transitions returns bare codes (e.g. 'assigned_to_franchisee').
// Option (a) from the plan: humanize client-side.
export function humanizeStatusCode(code: string): string {
  const spaced = code.replace(/_/g, ' ').trim();
  if (!spaced) return code;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

interface TransitionActionsPolicyHolder {
  name: string;
  phone: string;
  email?: string | null;
  user_id?: string | null;
}

interface TransitionActionsProps {
  jobId: string;
  currentStatusCode: string;
  policyHolder: TransitionActionsPolicyHolder;
}

export function TransitionActions({
  jobId,
  currentStatusCode,
  policyHolder,
}: TransitionActionsProps) {
  const flagEnabled = arePreInspectionTransitionsEnabled();
  const [userType, setUserType] = useState<string | null>(null);
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [assignmentTarget, setAssignmentTarget] =
    useState<AssignmentTransitionCode | null>(null);
  const [appointmentTarget, setAppointmentTarget] =
    useState<AppointmentTransitionCode | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setUserType(window.localStorage.getItem('user_type') ?? '');
  }, []);

  const canTransition =
    userType !== null &&
    (TRANSITION_ALLOWED_USER_TYPES as readonly string[])
      .map(normalize)
      .includes(normalize(userType));

  const query = useAllowedTransitions(currentStatusCode, {
    enabled: flagEnabled && canTransition,
  });

  const mutation = useUpdateJobStatus(jobId, {
    onSuccess: (data) => {
      setPendingTarget(null);
      setErrorMessage(null);
      setSuccessMessage(`Status updated to "${humanizeStatusCode(data.status)}".`);
    },
  });

  if (!flagEnabled) return null;
  if (userType === null) return null;
  if (!canTransition) return null;

  const wrapperClass = 'mt-4 border-t border-gray-100 pt-4';

  if (query.isLoading) {
    return (
      <div className={wrapperClass}>
        <div
          data-testid="transition-actions-loading"
          className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500"
        >
          Loading available actions…
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={wrapperClass}>
        <div
          role="alert"
          data-testid="transition-actions-error"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          Unable to load available actions.
        </div>
      </div>
    );
  }

  const transitions = query.data?.allowed_transitions ?? [];

  if (transitions.length === 0) {
    return (
      <div className={wrapperClass}>
        <div
          data-testid="transition-actions-empty"
          className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500"
        >
          No status changes are available from the current state.
        </div>
      </div>
    );
  }

  const handleClick = (target: string) => {
    setSuccessMessage(null);
    setErrorMessage(null);
    if (isAssignmentTransition(target)) {
      setAssignmentTarget(target);
      return;
    }
    if (isAppointmentTransition(target)) {
      setAppointmentTarget(target);
      return;
    }
    setPendingTarget(target);
    mutation.mutate(
      { status: target },
      {
        onError: (err) => {
          setPendingTarget(null);
          if (err instanceof PreInspectionApiError) {
            if (err.status === 409) {
              setErrorMessage(
                'The status changed while you were viewing this page. Refresh to see the latest.',
              );
              return;
            }
            setErrorMessage(err.message);
            return;
          }
          setErrorMessage('Failed to update status.');
        },
      },
    );
  };

  const disabled = mutation.isPending;

  return (
    <div className={wrapperClass}>
      <div data-testid="transition-actions" className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Update status:</span>
          {transitions.map((target) => {
            const isThisPending = disabled && pendingTarget === target;
            return (
              <button
                key={target}
                type="button"
                disabled={disabled}
                onClick={() => handleClick(target)}
                data-testid={`transition-button-${target}`}
                className="inline-flex items-center rounded-md border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300"
              >
                {isThisPending ? 'Updating…' : humanizeStatusCode(target)}
              </button>
            );
          })}
        </div>

        {successMessage && (
          <p
            role="status"
            data-testid="transition-actions-success"
            className="text-sm text-green-700"
          >
            {successMessage}
          </p>
        )}

        {errorMessage && (
          <p
            role="alert"
            data-testid="transition-actions-mutation-error"
            className="text-sm text-red-700"
          >
            {errorMessage}
          </p>
        )}
      </div>

      {assignmentTarget !== null && (
        <AssignmentActionModal
          jobId={jobId}
          toStatusCode={assignmentTarget}
          policyHolder={policyHolder}
          onClose={() => {
            setAssignmentTarget(null);
            setSuccessMessage(`Status updated.`);
          }}
        />
      )}

      {appointmentTarget !== null && (
        <AppointmentActionModal
          jobId={jobId}
          toStatusCode={appointmentTarget}
          onClose={() => {
            setAppointmentTarget(null);
            setSuccessMessage(`Status updated.`);
          }}
        />
      )}
    </div>
  );
}
